import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { faSquareStar } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faArrowUpFromBracket } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faCopy } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faBell } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { CHANNELS_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { BellCheck } from '@components/EmailNotifications/GetNotifiedButton/BellCheck';
import { DropdownMenuItem, MenuItemLabel } from '@components/basics/Dropdowns/GlobalDropdown';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { Button } from '@components/main-components/button/Button';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { useToast } from '@context/toast';
import type { ChannelNotificationStatus } from '@graphql/reactQuery';
import { type CurrentUserChannelJoinStatus } from '@graphql/reactQuery';
import { useCopy } from '@hooks/useCopy';
import { useLogError } from '@hooks/useLogErrorNew';
import { useStableCallback } from '@hooks/useStableCallback';
import { trackClickEvent } from '@lib/analytics';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER } from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { useChannelNotifications } from '../useChannelNotifications';
import { useIsChannelCurator } from '../useIsChannelCurator';
import { useJoinOrLeaveChannel } from '../useJoinOrLeaveChannel';

type Props = {
  channelId: string;
  channelWebappUri: string;
  settingsPath: string;
  joinStatus: Maybe<CurrentUserChannelJoinStatus>;
  notificationStatus: Maybe<ChannelNotificationStatus>;
  loading: boolean;
};

type JoinOrLeaveProps = {
  joiningOrLeaving: boolean;
  handleJoinOrLeaveChannel: () => void;
  isChannelOwner: boolean;
};

type SetNotificationProps = {
  settingNotificationPreferences: boolean;
  setNotificationPreferences: () => void;
};

function Mobile({
  channelId,
  channelWebappUri,
  settingsPath,
  joinStatus,
  handleJoinOrLeaveChannel,
  joiningOrLeaving,
  isChannelOwner,
  setNotificationPreferences,
  notificationStatus,
  settingNotificationPreferences,
}: Props & JoinOrLeaveProps & SetNotificationProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { copy } = useCopy({ text: channelWebappUri, successMessage: 'Link copied to clipboard' });

  return (
    <div className="flex items-center gap-2">
      <ThreeDotsDropdown isDropdownOpen={isDropdownOpen} setIsDropdownOpen={setIsDropdownOpen}>
        <DropdownMenuItem
          onClick={() => {
            copy();
            trackClickEvent({
              event: 'Share Channel Clicked',
              properties: {
                associationType: 'Channel',
                associationId: channelId,
                channelId,
              },
            });
          }}
        >
          <FontAwesomeIcon icon={faCopy} size="lg" />
          <MenuItemLabel>Copy link</MenuItemLabel>
        </DropdownMenuItem>

        {isChannelOwner && (
          <DropdownMenuItem>
            <AccessibleLink pathnameUri={webappUriPath(settingsPath)}>
              <FontAwesomeIcon icon={faSquareStar} size="lg" />
              <MenuItemLabel>Edit</MenuItemLabel>
            </AccessibleLink>
          </DropdownMenuItem>
        )}
      </ThreeDotsDropdown>

      {joinStatus === 'JOINED' && (
        <Button
          label="Join Status"
          leadingIcon={
            notificationStatus === 'ENABLED' ? (
              <span className=" [&_svg]:h-5 ">
                <BellCheck />
              </span>
            ) : (
              <FontAwesomeIcon icon={faBell} fontSize={20} />
            )
          }
          onClick={setNotificationPreferences}
          variant="tertiary"
          size="squared"
          loading={settingNotificationPreferences}
          className="h-9 min-h-[2.25rem] w-9 min-w-[2.25rem] px-0"
          iconOnly
        />
      )}

      {!isChannelOwner && (
        <Button
          label={joinStatus === 'JOINED' ? 'Joined' : 'Join'}
          variant={joinStatus === 'JOINED' ? 'tertiary' : 'primary'}
          loading={joiningOrLeaving}
          onClick={handleJoinOrLeaveChannel}
          size="full"
          className="h-9 font-title !text-title-xs"
        />
      )}
    </div>
  );
}

function Desktop({
  channelId,
  channelWebappUri,
  joinStatus,
  loading,
  settingsPath,
  handleJoinOrLeaveChannel,
  joiningOrLeaving,
  isChannelOwner,
  setNotificationPreferences,
  notificationStatus,
  settingNotificationPreferences,
}: Props & JoinOrLeaveProps & SetNotificationProps) {
  const { copy } = useCopy({ text: channelWebappUri, successMessage: 'Link copied to clipboard' });

  return (
    <div className="flex items-center gap-2">
      <IconButton
        icon={
          <FontAwesomeIcon icon={faArrowUpFromBracket} className="text-base500" fontSize={22} />
        }
        onClick={() => {
          copy();
          trackClickEvent({
            event: 'Share Channel Clicked',
            properties: {
              associationType: 'Channel',
              associationId: channelId,
              channelId,
            },
          });
        }}
      />

      {joinStatus === 'JOINED' && (
        <Button
          label="Join Status"
          leadingIcon={
            notificationStatus === 'ENABLED' ? (
              <span className=" [&_svg]:h-5 ">
                <BellCheck />
              </span>
            ) : (
              <FontAwesomeIcon icon={faBell} fontSize={20} />
            )
          }
          onClick={setNotificationPreferences}
          variant="tertiary"
          size="squared"
          loading={settingNotificationPreferences}
          className="h-9 min-h-[2.25rem] w-9 min-w-[2.25rem] px-0"
          iconOnly
        />
      )}

      {!isChannelOwner && (
        <Button
          label={joinStatus === 'JOINED' ? 'Joined' : 'Join'}
          variant={joinStatus === 'JOINED' ? 'tertiary' : 'primary'}
          loading={joiningOrLeaving}
          onClick={handleJoinOrLeaveChannel}
          size="full"
          className="h-9 font-title !text-title-xs"
        />
      )}

      {isChannelOwner && (
        <Button
          label="Edit"
          variant="primary"
          href={webappUriPath(settingsPath)}
          loading={loading}
          size="full"
          className="h-9 font-title !text-title-xs"
        />
      )}
    </div>
  );
}

export const ChannelActionBar = (props: Props) => {
  const { authUser } = useAuth();

  const { channelId, joinStatus, notificationStatus } = props;

  const logError = useLogError();

  const { openToast } = useToast();

  const { isLoading, joinOrLeaveChannel } = useJoinOrLeaveChannel({ channelId, joinStatus });

  const { isChannelOwner } = useIsChannelCurator({ channelId });

  const { setChannelNotificationPreferences, isLoading: isSettingNotifications } =
    useChannelNotifications();

  const handleSetNotifications = useStableCallback(async () => {
    if (!authUser) {
      triggerLogin();
      return;
    }

    if (joinStatus === 'NOT_JOINED') {
      return;
    }

    try {
      const result = await setChannelNotificationPreferences({
        channelId,
        status: notificationStatus === 'ENABLED' ? 'DISABLED' : 'ENABLED',
      });

      if (
        result.data.setUserNotificationPreferencesOnChannel.__typename ===
        'MutationSetUserNotificationPreferencesOnChannelSuccess'
      ) {
        openToast({
          text:
            notificationStatus === 'ENABLED'
              ? "You won't receive notifications from this channel anymore."
              : "You'll receive notifications from this channel.",
          variant: 'success',
        });
        return;
      }

      logError({
        action: CHANNELS_ERROR_ACTIONS.CHANNEL_NOTIFICATION_PREFERENCES_FAILED,
        error: new Error(result.data.setUserNotificationPreferencesOnChannel.message),
        errorType: 'CHANNELS',
        isArtistOnlyPage: false,
        level: 'error',
        message: result.data.setUserNotificationPreferencesOnChannel.message,
        toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
        pillar: 'CHANNELS',
      });
      return;
    } catch (error) {
      const message = 'There was an error setting the channel notification preferences.';
      logError({
        action: CHANNELS_ERROR_ACTIONS.CHANNEL_NOTIFICATION_PREFERENCES_FAILED,
        error,
        errorType: 'CHANNELS',
        isArtistOnlyPage: false,
        level: 'error',
        message,
        toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
        pillar: 'CHANNELS',
      });
    }
  });

  return (
    <>
      <div className="block lg:hidden">
        <Mobile
          joiningOrLeaving={isLoading}
          handleJoinOrLeaveChannel={joinOrLeaveChannel}
          isChannelOwner={isChannelOwner}
          setNotificationPreferences={handleSetNotifications}
          settingNotificationPreferences={isSettingNotifications}
          {...props}
        />
      </div>
      <div className="hidden lg:block">
        <Desktop
          joiningOrLeaving={isLoading}
          handleJoinOrLeaveChannel={joinOrLeaveChannel}
          isChannelOwner={isChannelOwner}
          setNotificationPreferences={handleSetNotifications}
          settingNotificationPreferences={isSettingNotifications}
          {...props}
        />
      </div>
    </>
  );
};
